import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import queryString from 'query-string'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { TransactionStatus } from 'src/models/transaction.models'
import { savedViewsQueryKeys } from 'src/queries/queries.constants'
import { useExtendDelete, useRequestConfig } from 'src/queries/queries.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

export interface GetSavedViewsQueryParams {
  count?: number
  page?: number
}

export interface GetSavedViewsSuccessPayload {
  userSavedViews: SavedViewsModel[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
  }
}

// This is for transactions
// may need to be revisited if we add saved views to other tables like vcs
export interface SavedViewsModel {
  id: string
  viewType: TransactionReportType
  displayName: string
  filters: TransactionsFiltersBody
  columns?: {
    visibleColumns?: Array<string>
  }
  columnData?: unknown
}

export interface CreateSavedViewRequestBody {
  viewType?: UserSavedViewType
  displayName: string
  filters: TransactionsFiltersBody
  columnData?: unknown
}

interface CreateSavedViewApiRequestBody {
  viewType: UserSavedViewType
  displayName: string
  filters: UserSavedViewFiltersRequest
  columnData?: unknown
}

interface CreateSavedViewVariables {
  context: TransactionReportType
  body: CreateSavedViewRequestBody
}

export enum UserSavedViewType {
  ACCOUNT = 'ACCOUNT',
  MY_TRANSACTIONS = 'MY_TRANSACTIONS',
  EXPENSES = 'EXPENSES',
  BUDGET = 'BUDGET',
  EXPENSE_REPORT = 'EXPENSE_REPORT',
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  ACCOUNTING_TRANSACTIONS = 'ACCOUNTING_TRANSACTIONS',
  OTHER = 'OTHER',
}

export interface UserSavedViewFiltersRequest {
  dateRangeType?: string
  dateType?: string
  search?: string
  since?: string
  until?: string
  sort?: string
  sinceClearedAt?: string
  untilClearedAt?: string
  minClearingBillingCents?: string
  maxClearingBillingCents?: string
  status?: string[]
  source?: string[]
  connectedPlatform?: string
  expenseCategoryStatuses?: string[]
  receiptStatus?: string[]
  receiptRequired?: string
  syncStatuses?: string[]
  recipientId?: string[]
  creditCardId?: string[]
  mccGroup?: string[]
  sinceTimestamp?: string
  untilTimestamp?: string
  statuses?: string[]
  organizationRoles?: string[]
  showDeactivatedUsers?: string
  owners?: string[]
  users?: string[]
  type?: string
  sortField?: string
  user?: string[]
  cardTypes?: string[]
  issued?: string
  recipients?: string[]
  recipient?: string
  sortDirection?: string
  deactivatingIn?: string
  sourceUserOrViewerOrDelegateUser?: string
  page?: string
  count?: string
  perPage?: string
}

const mapTransactionContextToViewType = (
  context: TransactionReportType,
): UserSavedViewType => {
  switch (context) {
    case TransactionReportType.Account:
      return UserSavedViewType.ACCOUNT
    case TransactionReportType.MyTransactions:
      return UserSavedViewType.MY_TRANSACTIONS
    case TransactionReportType.Expenses:
      return UserSavedViewType.EXPENSES
    case TransactionReportType.Budget:
      return UserSavedViewType.BUDGET
    case TransactionReportType.ExpenseReport:
      return UserSavedViewType.EXPENSE_REPORT
    case TransactionReportType.NeedsApproval:
      return UserSavedViewType.NEEDS_APPROVAL
    case TransactionReportType.AccountingTransactions:
      return UserSavedViewType.ACCOUNTING_TRANSACTIONS
    case TransactionReportType.Other:
    default:
      return UserSavedViewType.OTHER
  }
}

const mapFiltersToCreateSavedViewRequest = (
  filters: TransactionsFiltersBody,
): UserSavedViewFiltersRequest => {
  const selectedStatuses = filters.status
    ? Array.from(new Set(filters.status))
    : []
  const defaultStatuses = new Set<string>([
    TransactionStatus.Pending,
    TransactionStatus.Cleared,
    TransactionStatus.Declined,
    TransactionStatus.AuthReversal,
  ])
  const hasAllDefaultStatuses =
    selectedStatuses.length === defaultStatuses.size &&
    selectedStatuses.every((status) => defaultStatuses.has(status))
  const status =
    selectedStatuses.length === 0 || hasAllDefaultStatuses
      ? undefined
      : selectedStatuses

  return {
    dateRangeType: filters.dateRangeType,
    dateType: filters.dateType,
    search: filters.search,
    since: filters.since,
    until: filters.until,
    sort: filters.sort,
    sinceClearedAt: filters.sinceClearedAt,
    untilClearedAt: filters.untilClearedAt,
    minClearingBillingCents:
      filters.minClearingBillingCents !== undefined
        ? String(filters.minClearingBillingCents)
        : undefined,
    maxClearingBillingCents:
      filters.maxClearingBillingCents !== undefined
        ? String(filters.maxClearingBillingCents)
        : undefined,
    status,
    source: filters.source,
    connectedPlatform: filters.connectedPlatform,
    expenseCategoryStatuses: filters.expenseCategoryStatuses,
    receiptStatus: filters.receiptStatus,
    receiptRequired:
      filters.receiptRequired !== undefined
        ? String(filters.receiptRequired)
        : undefined,
    syncStatuses: filters.syncStatuses,
    recipientId: filters.recipientOrPhysicalCardUserId,
    creditCardId: filters.creditCardId,
    mccGroup: filters.mccGroup,
  }
}

const extractCreatedSavedView = (data: unknown): SavedViewsModel => {
  const candidate =
    (data as any)?.userSavedView ?? (data as any)?.savedView ?? data
  if (!candidate || typeof (candidate as any).id !== 'string') {
    throw new Error('Invalid saved view create response')
  }
  return candidate as SavedViewsModel
}

export const useFetchSavedViews = (
  context: TransactionReportType,
  queryParameters?: GetSavedViewsQueryParams,
  options?: Partial<
    UseInfiniteQueryOptions<
      GetSavedViewsSuccessPayload,
      AxiosError<BackendErrorPayload<string>>,
      InfiniteData<GetSavedViewsSuccessPayload>,
      GetSavedViewsSuccessPayload,
      readonly unknown[],
      number
    >
  >,
) => {
  const requestConfig = useRequestConfig()
  return useInfiniteQuery({
    ...options,
    queryKey: savedViewsQueryKeys.list(context, queryParameters),
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...queryParameters,
        count: queryParameters?.count ?? 50,
        page: pageParam,
      }
      return await axios
        .get(
          `/usersavedviews/${context}?${queryString.stringify(params)}`,
          requestConfig,
        )
        .then((response) => {
          return response.data as GetSavedViewsSuccessPayload
        })
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.numberOfPages - 1) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    select: (
      response: InfiniteData<GetSavedViewsSuccessPayload, number>,
    ): GetSavedViewsSuccessPayload => ({
      userSavedViews: response?.pages.flatMap((page) => page.userSavedViews),
      pagination: {
        page: response?.pages[0]?.pagination.page,
        numberOfPages: response?.pages[0]?.pagination.numberOfPages,
        pageItemCount: response?.pages[0]?.pagination.pageItemCount,
      },
    }),
    initialPageParam: 0,
  })
}

export const useCreateSavedView = (
  options?: Partial<
    UseMutationOptions<
      SavedViewsModel,
      AxiosError<BackendErrorPayload<string>>,
      CreateSavedViewVariables
    >
  >,
) => {
  const requestConfig = useRequestConfig()
  return useMutation({
    ...options,
    mutationFn: async ({ context, body }) => {
      const requestBody: CreateSavedViewApiRequestBody = {
        viewType: mapTransactionContextToViewType(context),
        displayName: body.displayName,
        filters: mapFiltersToCreateSavedViewRequest(body.filters),
        columnData: body.columnData,
      }

      return await axios
        .post('/usersavedviews', requestBody, requestConfig)
        .then((response) => extractCreatedSavedView(response.data))
    },
  })
}

export const useDeleteSavedView = (context: TransactionReportType) => {
  const queryClient = useQueryClient()
  return useExtendDelete<{}, { body: { savedViewId: string } }>({
    mutationUrl: (vars) => `/usersavedviews/${vars.body.savedViewId}`,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: savedViewsQueryKeys.list(context),
      })
    },
  })
}
